import { IonButton } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../ui/Avatar';
import { MarkReadButton } from './MarkReadButton';
import { SwipeToRead } from './SwipeToRead';
import {
  useAcceptConnection,
  useMyConnections,
  useRejectConnection,
} from '../../hooks/useConnections';
import type { RecipientActivity } from '../../types/activity';

interface ConnectionMeta {
  connectionId?: string;
  actorDisplayName?: string;
  actorAvatarUrl?: string;
  event?: string;
}

interface ActivityConnectionCardProps {
  activity: RecipientActivity;
  /** True in the "new"/unread tab — gates the inline actions + dismiss. */
  unread: boolean;
  /** Mark the activity read and reload the list (parent owns the fetch). */
  onMarkRead: (activityId: string) => void;
  /** Close the sheet and navigate (used for the non-actionable fallback link). */
  onRoute: (route: string) => void;
}

/**
 * Activity-center card for a connection event. For an unread *request* it
 * exposes Accept / Decline inline (same actions as the `/c={id}` page), so the
 * recipient can respond without leaving the activity center. Accepted-type
 * events (and already-read requests) render as a simple message with a
 * "check it out" link.
 */
export const ActivityConnectionCard: React.FC<ActivityConnectionCardProps> = ({
  activity,
  unread,
  onMarkRead,
  onRoute,
}) => {
  const { t } = useTranslation(['activity', 'connections']);
  const accept = useAcceptConnection();
  const reject = useRejectConnection();

  const meta = (activity.metadata?.connection ?? {}) as ConnectionMeta;
  const connectionId = meta.connectionId;
  const isRequest = meta.event === 'connection_requested';
  const dp = activity.displayProfiles?.[0];
  const name = meta.actorDisplayName || dp?.displayName || '';
  const avatarUrl = meta.actorAvatarUrl || dp?.avatarUrl;
  const isActing = accept.isPending || reject.isPending;

  // Inline actions only make sense for an unhandled (unread) request we can
  // identify; everything else falls back to the message + route link.
  const showActions = unread && isRequest && !!connectionId;

  // In the read/completed tab a request should read as its outcome, not as the
  // original prompt. The outcome isn't stored on the activity, so derive it
  // from the connection's current state: accepted → accepted, rejected →
  // declined, still-pending/gone → dismissed (the user X'd it). Only fetched
  // when we actually need it (a read request card).
  const needsOutcome = !unread && isRequest;
  const { data: allConnections } = useMyConnections('all', { enabled: needsOutcome });

  let displayMessage = activity.message;
  if (needsOutcome && allConnections) {
    const conn = allConnections.find((c) => c.id === connectionId);
    if (conn?.acceptedAt) {
      displayMessage = t('connections:result.accepted', { name });
    } else if (conn?.rejectedAt) {
      displayMessage = t('connections:result.declined', { name });
    } else {
      displayMessage = t('connections:result.dismissed', { name });
    }
  }

  const handleAccept = () => {
    if (!connectionId) return;
    accept.mutate(connectionId, { onSuccess: () => onMarkRead(activity.id) });
  };
  const handleDecline = () => {
    if (!connectionId) return;
    reject.mutate(connectionId, { onSuccess: () => onMarkRead(activity.id) });
  };

  return (
    <SwipeToRead
      onCommit={() => onMarkRead(activity.id)}
      label={t('activity:card.markAsRead')}
      disabled={!unread}
    >
    <div className="relative border border-border rounded-lg p-4">
      <span className="mb-2 block pr-9 text-xs font-medium uppercase text-muted-foreground">
        {t('connections:section.title')}
      </span>

      {/* Unread: tapping the body opens the connection page (`/c={id}` for a
          request, Profile for an accepted event). Read/completed: a static row
          showing the outcome — not clickable, since routing a settled request
          back into resolve would clone a fresh row. */}
      {unread ? (
        <button
          type="button"
          onClick={() => activity.route && onRoute(activity.route)}
          disabled={!activity.route}
          className="flex w-full items-center gap-3 pr-9 text-left"
        >
          <Avatar src={avatarUrl} name={name} size="md" />
          <p className="text-sm font-medium flex-1 min-w-0">{displayMessage}</p>
        </button>
      ) : (
        <div className="flex w-full items-center gap-3">
          <Avatar src={avatarUrl} name={name} size="md" />
          <p className="text-sm font-medium flex-1 min-w-0">{displayMessage}</p>
        </div>
      )}

      {showActions && (
        <div className="mt-3 flex gap-2">
          <IonButton
            size="small"
            fill="outline"
            color="medium"
            disabled={isActing}
            onClick={handleDecline}
          >
            {t('connections:requests.decline')}
          </IonButton>
          <IonButton size="small" disabled={isActing} onClick={handleAccept}>
            {t('connections:requests.accept')}
          </IonButton>
        </div>
      )}

      {unread && (
        <MarkReadButton
          onClick={() => onMarkRead(activity.id)}
          label={t('activity:card.markAsRead')}
          className="absolute top-2 right-2"
        />
      )}
    </div>
    </SwipeToRead>
  );
};
